import React from 'react';

import { FormattedMessage } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives';
import Box from '@atlaskit/primitives/box';

import { RovoLogoFromLogo, type RovoLogoProps } from '../../constants/icons';
import type { ProductConfig, ProductKeys } from '../../constants/products';
import {
	type BannerType,
	BannerType as BannerTypeMap,
} from '../../utils/bulk-connect-banner/types';
import type { MessageContent } from '../oauth-message';

import { messages } from './messages';

const maxDisplayedProducts = 3;

const styles = xcss({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
});

const initialPositionStyles = xcss({
	display: 'flex',
	marginLeft: 'space.0',
});

const followingPositionStyles = xcss({
	display: 'flex',
	marginLeft: 'space.negative.075',
});

const getProductDisplayName = (
	product: ProductKeys,
	products: Record<ProductKeys, ProductConfig>,
): React.ReactNode => {
	return product === 'sharepoint' ? 'SharePoint' : products[product]?.displayName || '';
};

export const getBannerContent = (
	newlyAddedProducts: ProductKeys[],
	allProducts: Record<ProductKeys, ProductConfig>,
	bannerType: BannerType,
): MessageContent | null => {
	if (bannerType === BannerTypeMap.NONE) {
		return null;
	}

	const groupIcons = (props: RovoLogoProps): JSX.Element => (
		<Box xcss={styles}>
			{newlyAddedProducts.slice(0, maxDisplayedProducts).map((product, index) => {
				return (
					<Box
						key={product}
						xcss={index === 0 ? initialPositionStyles : followingPositionStyles}
						testId={product}
					>
						{RovoLogoFromLogo(allProducts[product]?.Icon)(props)}
					</Box>
				);
			})}
		</Box>
	);

	if (bannerType === BannerTypeMap.INITIAL) {
		return {
			title: <FormattedMessage {...messages.initialBannerTitle} />,
			description: null,
			connectText: <FormattedMessage {...messages.initialBannerConnectText} />,
			MessageIcon: groupIcons,
			iconNeedsPadding: true,
		};
	}

	// it is a secondary banner
	const newlyAddedProductCount = newlyAddedProducts.length;
	if (newlyAddedProductCount === 1) {
		const product = newlyAddedProducts[0];
		const productString = getProductDisplayName(product, allProducts);
		const Icon = allProducts[product]?.Icon;

		return {
			title: (
				<FormattedMessage
					{...messages.singleProductBannerTitle}
					values={{ product: productString }}
				/>
			),
			description: (
				<FormattedMessage
					{...messages.singleProductBannerDescription}
					values={{ product: productString }}
				/>
			),
			connectText: <FormattedMessage {...messages.singleProductBannerConnectText} />,
			MessageIcon: Icon,
		};
	}

	// it is a secondary banner with multiple new products

	return {
		title: (
			<FormattedMessage
				{...messages.multipleProductsBannerTitle}
				values={{ count: newlyAddedProductCount }}
			/>
		),
		description: <FormattedMessage {...messages.multipleProductsBannerDescription} />,
		connectText: <FormattedMessage {...messages.multipleProductsBannerConnectText} />,
		MessageIcon: groupIcons,
		iconNeedsPadding: true,
	};
};
